<template>
  <div class="dca-panel" data-testid="dca-allocation-panel">
    <div class="d-flex align-start justify-space-between mb-2">
      <div>
        <div class="text-subtitle-1 font-weight-bold">{{ segmentLabel }}</div>
        <div class="text-caption text-medium-emphasis">
          {{ placementLabel }}
        </div>
      </div>
      <SoButton
        type="icon"
        icon="close"
        aria-label="Close"
        data-testid="dca-panel-close"
        @click="$emit('close')"
      />
    </div>

    <p
      v-if="draftAllocations.length === 0"
      class="text-caption text-medium-emphasis mb-4"
      data-testid="dca-panel-empty"
    >
      No agencies configured for this step. Add one below to start a split.
    </p>

    <div
      v-for="(allocation, index) in draftAllocations"
      :key="allocation.dca"
      class="dca-panel__row mb-3"
      :data-testid="`dca-panel-row-${allocation.dca}`"
    >
      <div class="d-flex align-center justify-space-between mb-1">
        <span class="d-flex align-center">
          <span
            class="dca-panel__dot mr-2"
            :style="{ backgroundColor: colourFor(allocation.dca) }"
          />
          <span class="text-body-2">{{ nameFor(allocation.dca) }}</span>
        </span>
        <div class="d-flex align-center ga-1">
          <SoNumberField
            :model-value="allocation.percentage"
            :min="0"
            :max="100"
            :precision="0"
            hide-details
            suffix="%"
            control-variant="hidden"
            class="dca-panel__field"
            :aria-label="`${nameFor(allocation.dca)} percentage`"
            :data-testid="`dca-panel-input-${allocation.dca}`"
            @update:model-value="onPercentageChange(index, $event)"
          />
          <SoButton
            type="icon"
            icon="delete"
            :aria-label="`Remove ${nameFor(allocation.dca)}`"
            :data-testid="`dca-panel-remove-${allocation.dca}`"
            @click="removeAgency(index)"
          />
        </div>
      </div>
      <SoSlider
        :model-value="allocation.percentage"
        :min="0"
        :max="100"
        :step="1"
        :data-testid="`dca-panel-slider-${allocation.dca}`"
        @update:model-value="onPercentageChange(index, $event)"
      />
    </div>

    <SoSelect
      v-if="availableAgencies.length"
      :model-value="null"
      :items="availableAgencies"
      label="Add an agency"
      item-title="title"
      item-value="value"
      class="mb-3"
      data-testid="dca-panel-add"
      @update:model-value="addAgency"
    />

    <div class="d-flex justify-space-between align-center mb-3">
      <span class="text-body-2 font-weight-bold">Total</span>
      <!--
        The icon carries the same meaning as the colour. Colour alone fails WCAG 1.4.1
        and is invisible to anyone who cannot distinguish green from red, and this is
        the control that decides whether a save is allowed at all.

        aria-live so the running total is announced as sliders move - otherwise the
        state a keyboard or screen-reader user is steering towards is silent.
      -->
      <span
        class="d-flex align-center ga-1 text-body-2 font-weight-bold"
        :class="totalValid ? 'text-success' : 'text-error'"
        aria-live="polite"
        data-testid="dca-panel-total"
      >
        <SoIcon
          :icon="totalValid ? 'check' : 'alert-circle'"
          size="small"
          :data-testid="
            totalValid ? 'dca-panel-total-ok' : 'dca-panel-total-warn'
          "
        />
        {{ total }}%
      </span>
    </div>

    <!--
      Names the gap rather than leaving the user to work out 100 minus the total. The
      gateway takes exactly 100 or nothing, so landing precisely is the whole task -
      and with three or more agencies the arithmetic is where the fiddliness lives.
    -->
    <p
      v-if="remaining !== 0 && draftAllocations.length > 0"
      class="text-caption text-medium-emphasis mb-3"
      data-testid="dca-panel-remaining"
    >
      {{ remainingMessage }}
    </p>

    <SoAlert
      v-if="!totalValid"
      type="error"
      class="mb-3"
      data-testid="dca-panel-total-error"
    >
      A split must add up to 100%. Currently {{ total }}%. To retire this step
      entirely, remove every agency.
    </SoAlert>

    <SoAlert
      v-if="willClearStep"
      type="warning"
      class="mb-3"
      data-testid="dca-panel-clear-warning"
    >
      Saving with no agencies retires this step. No accounts will be placed into
      it.
    </SoAlert>

    <p
      class="text-caption text-medium-emphasis mb-3"
      data-testid="dca-panel-effective"
    >
      Changes apply from the next DCA allocation run, {{ nextRun }}. A run
      already under way will not pick them up.
    </p>

    <div class="d-flex ga-2 mb-4">
      <SoButton
        type="secondary"
        title="Reset"
        :disabled="!isDirty"
        data-testid="dca-panel-reset"
        @click="reset"
      />
      <SoButton
        type="primary"
        :title="willClearStep ? 'Retire step' : 'Save split'"
        :disabled="!canSave"
        :loading="saving"
        data-testid="dca-panel-save"
        @click="save"
      />
    </div>

    <DcaAllocationHistory
      :changes="changes"
      :loading="historyLoading"
      :error="historyError"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoNumberField from "@/components/formControls/SoNumberField.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoSlider from "@/components/SoSlider.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import DcaAllocationHistory from "./DcaAllocationHistory.vue"
  import {
    IDcaAllocation,
    IDcaAllocationChange,
  } from "@/models/dcaAllocation/dcaAllocation.types"
  import { isValidTotal, TOTAL_REQUIRED } from "../dcaAllocation.config"
  import {
    agencyColour,
    agencyName,
    AGENCY_COLOURS,
    UNSELECTABLE_AGENCIES,
  } from "../dcaAgencyColours"
  import { formatRun, nextAllocationRun } from "../dcaAllocationSchedule"

  export default defineComponent({
    components: {
      SoButton,
      SoNumberField,
      SoSelect,
      SoSlider,
      SoAlert,
      SoIcon,
      DcaAllocationHistory,
    },
    props: {
      segmentLabel: { type: String, required: true },
      placementLabel: { type: String, required: true },
      allocations: {
        type: Array as PropType<IDcaAllocation[]>,
        default: () => [],
      },
      saving: { type: Boolean, default: false },
      changes: {
        type: Array as PropType<IDcaAllocationChange[]>,
        default: () => [],
      },
      historyLoading: { type: Boolean, default: false },
      historyError: { type: Boolean, default: false },
    },
    emits: ["close", "save"],
    data() {
      return {
        draftAllocations: [] as IDcaAllocation[],
        /**
         * Ticked so the stated run time cannot go stale while the panel sits open -
         * otherwise the panel says 17:00 and the confirmation afterwards says 18:00.
         */
        now: new Date(),
        clock: undefined as ReturnType<typeof setInterval> | undefined,
      }
    },
    computed: {
      total(): number {
        return this.draftAllocations.reduce(
          (total, allocation) => total + (Number(allocation.percentage) || 0),
          0,
        )
      },
      totalValid(): boolean {
        return isValidTotal(this.total, this.draftAllocations.length)
      },
      /**
       * An empty list is the endpoint's "retire this step" operation. A list of
       * agencies at 0 is NOT — it totals 0 and the gateway rejects it.
       */
      willClearStep(): boolean {
        return this.draftAllocations.length === 0 && this.allocations.length > 0
      },
      isDirty(): boolean {
        return (
          JSON.stringify(this.draftAllocations) !==
          JSON.stringify(this.allocations)
        )
      },
      /**
       * `saving` is included because SoButton resolves `disabled ?? loading`, so an
       * explicit `disabled: false` means a loading button stays clickable - and a second
       * click on an in-flight save would write the same split twice.
       */
      canSave(): boolean {
        return this.totalValid && this.isDirty && !this.saving
      },
      /** Positive means volume still to allocate, negative means over-allocated. */
      remaining(): number {
        return TOTAL_REQUIRED - this.total
      },
      remainingMessage(): string {
        return this.remaining > 0
          ? `${this.remaining}% left to allocate.`
          : `${Math.abs(this.remaining)}% over - reduce a share by that much.`
      },
      nextRun(): string {
        return formatRun(nextAllocationRun(this.now))
      },
      availableAgencies(): { title: string; value: string }[] {
        const alreadyInSplit = new Set(
          this.draftAllocations.map((allocation) => allocation.dca),
        )
        // The values offered here are written straight back as `dca`, which is a
        // foreign key to dca_configuration_v2 - so they have to be the agency names
        // the registry holds, not Junifer ticket codes.
        return Object.keys(AGENCY_COLOURS)
          .filter((dca) => !UNSELECTABLE_AGENCIES.includes(dca))
          .filter((dca) => !alreadyInSplit.has(dca))
          .map((dca) => ({ title: agencyName(dca), value: dca }))
      },
    },
    watch: {
      allocations: {
        immediate: true,
        handler() {
          this.reset()
        },
      },
    },
    mounted() {
      this.clock = setInterval(() => {
        this.now = new Date()
      }, 30_000)
    },
    beforeUnmount() {
      clearInterval(this.clock)
    },
    methods: {
      colourFor(dca: string): string {
        return agencyColour(dca)
      },
      nameFor(dca: string): string {
        return agencyName(dca)
      },
      reset() {
        this.draftAllocations = this.allocations.map((allocation) => ({
          ...allocation,
        }))
      },
      onPercentageChange(index: number, value: number | string) {
        const clamped = Math.max(
          0,
          Math.min(100, Math.round(Number(value) || 0)),
        )
        this.draftAllocations[index].percentage = clamped
      },
      addAgency(dca: string | null) {
        if (!dca) {
          return
        }
        this.draftAllocations.push({ dca, percentage: 0 })
      },
      removeAgency(index: number) {
        this.draftAllocations.splice(index, 1)
      },
      save() {
        if (!this.canSave) {
          return
        }
        this.$emit(
          "save",
          this.draftAllocations.map((allocation) => ({ ...allocation })),
        )
      },
    },
  })
</script>

<style scoped>
  .dca-panel__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .dca-panel__field {
    width: 116px;
  }
</style>
